/* eslint-disable @typescript-eslint/no-explicit-any */
import { CHECKOUT_STUDENT_FLOW } from "@/ts/enums/CheckoutStudentFlowEnum";
import { AddSubjectChangeApiInterface } from "@/ts/interfaces/Checkout/api/AddSubjectChangeApiInterface";
import { ValidateCouponResponseInterface } from "@/ts/interfaces/Checkout/api/ValidateCouponResponseInterface";
import { atom } from "jotai";
import { atomsWithMutation, atomsWithQuery } from "jotai-tanstack-query";
import { atomWithStorage, createJSONStorage, loadable } from "jotai/utils";
import { AsyncStorage } from "node_modules/jotai/vanilla/utils/atomWithStorage";
import { v4 as uuidv4 } from "uuid";

import { CheckoutInterface } from "@ts/interfaces/Checkout/CheckoutInterface";
import { PageQueryParamsInterface } from "@ts/interfaces/Checkout/PageQueryParamsInterface";
import { UserCheckoutStateInterface } from "@ts/interfaces/Checkout/UserCheckoutStateInterface";
import { AddPremiumServiceApiInterface } from "@ts/interfaces/Checkout/api/AddPremiumServiceApiInterface";
import { CreatePIApiInterface } from "@ts/interfaces/Checkout/api/CreatePIApiInterface";
import { CreateSubscriptionApiInterface } from "@ts/interfaces/Checkout/api/CreateSubscriptionApiInterface";
import { UserCheckoutStateApiInterface } from "@ts/interfaces/Checkout/api/UserCheckoutStateApiInterface";
import { ValidateCouponApiInterface } from "@ts/interfaces/Checkout/api/ValidateCouponApiInterface";
import { ApiResponseInterface } from "@ts/interfaces/Misc/api/ApiResponseInterface";

import {
    fetchCheckoutStateApi,
    postAddPremiumServiceApi,
    postAddSubjectChangesApi,
    postCheckoutStateApi,
    postCreateCbSubscriptionApi,
    postValidateCouponApi
} from "@api/checkoutApi";

export const parentIdAtom = atom(uuidv4());

const storage = createJSONStorage(() => sessionStorage);
export const checkoutAtom = atomWithStorage<CheckoutInterface>(
    "eukaCheckoutData",
    {
        selectedStudent: null,
        productFamily: null,
        plan: null,
        charge: null,
        addon: null,
        grade: null,
        user: {
            firstName: "",
            email: "",
            receiveOfferNews: false
        },
        nextAttemptPage: null,
        student: null
    },
    storage as AsyncStorage<CheckoutInterface>,
    { getOnInit: true }
);

export const isPaymentPendingAtom = atom<boolean>(false);

export const isNewsletterSubEnabledAtom = atom<boolean>(false);

export const canAddonSelectionSkipAtom = atom<boolean>(false);

export const isProvidedQueryParamsAtom = atom<boolean>(true);

export const pageQueryParamsAtom = atom<PageQueryParamsInterface>({
    family: null,
    grade: null,
    plan: null,
    addon: null,
    coupon: null,
    selectedCountry: null,
    selectedState: null,
    skip: null,
    origin: null,
    selectedLang: null
});

export const checkoutTotalAmountAtom = atom<number>(0);

export const couponAtom = atom<string | null>(null);

export const canShowPaymentSuccessPageAtom = atom<boolean>(false);

export const canShowExistingUserPageAtom = atom<boolean>(false);

export const userCheckoutStateTokenAtom = atom<string | null>(null);

export const checkoutStudentFlowAtom = atom<CHECKOUT_STUDENT_FLOW | null>(null);

const [userCheckoutApiStateAtom] = atomsWithQuery<
    ApiResponseInterface<{
        token: string;
        token_data: UserCheckoutStateInterface;
        username: string;
    }>
>(get => {
    const userCheckoutStateToken = get(userCheckoutStateTokenAtom);
    return {
        queryKey: ["userCheckoutState"],
        queryFn: async () =>
            (
                await fetchCheckoutStateApi(
                    (userCheckoutStateToken as string) ?? ""
                )
            ).data,
        //  Configuration for react query to specify how long the data fetched from this query remains "stale"
        staleTime: Infinity,
        enabled: !!userCheckoutStateToken
    };
});

export const [, createPaymentIntentAtom] = atomsWithMutation(() => ({
    mutationKey: ["createPaymentIntent"],
    mutationFn: async (
        data: CreatePIApiInterface
    ): Promise<
        ApiResponseInterface<{ id: string; gateway_account_id: string }>
    > => {
        return (await postCreateCbSubscriptionApi(data)).data;
    }
}));

export const [, addAddonAtom] = atomsWithMutation(() => ({
    mutationKey: ["addAddon"],
    mutationFn: async (data: AddPremiumServiceApiInterface) => {
        return (await postAddPremiumServiceApi(data)).data;
    }
}));

export const [, addSubjectChangesAtom] = atomsWithMutation(() => ({
    mutationKey: ["addSubjectChanges"],
    mutationFn: async (data: AddSubjectChangeApiInterface) => {
        return (await postAddSubjectChangesApi(data)).data;
    }
}));

export const [, createPaymentIntentForAddonAtom] = atomsWithMutation(() => ({
    mutationKey: ["createPaymentIntentForAddon"],
    mutationFn: async (
        data: AddPremiumServiceApiInterface
    ): Promise<
        ApiResponseInterface<{ id: string; gateway_account_id: string }>
    > => {
        return (await postAddPremiumServiceApi(data)).data;
    }
}));

export const [, createPaymentIntentForSubjectChangesAtom] = atomsWithMutation(
    () => ({
        mutationKey: ["createPaymentIntentForSubjectChanges"],
        mutationFn: async (
            data: AddSubjectChangeApiInterface
        ): Promise<
            ApiResponseInterface<{ id: string; gateway_account_id: string }>
        > => {
            return (await postAddSubjectChangesApi(data)).data;
        }
    })
);

export const [, createSubscriptionAtom] = atomsWithMutation(() => ({
    mutationKey: ["createSubscription"],
    mutationFn: async (data: CreateSubscriptionApiInterface) => {
        return (await postCreateCbSubscriptionApi(data)).data;
    }
}));

export const [, validateCouponAtom] = atomsWithMutation(() => ({
    mutationKey: ["validateCoupon"],
    mutationFn: async (
        data: ValidateCouponApiInterface
    ): Promise<ApiResponseInterface<ValidateCouponResponseInterface>> => {
        return (await postValidateCouponApi(data)).data;
    }
}));

export const userCheckoutStateAtom = atom<UserCheckoutStateInterface>(get => {
    const checkout = get(checkoutAtom) as CheckoutInterface;
    const atomData: UserCheckoutStateInterface = {
        USER_INFO_PAGE: {
            email: checkout.user.email,
            firstName: checkout.user.firstName,
            receiveOfferNews: checkout.user.receiveOfferNews
        },
        STUDENT_SELECTION_PAGE: checkout.selectedStudent
            ? { id: checkout.selectedStudent.id }
            : null,
        CREATE_NEW_STUDENT_PAGE: checkout.student
            ? {
                  firstName: checkout.student.firstName,
                  lastName: checkout.student.lastName
              }
            : null,
        PRODUCT_FAMILY_SELECT_PAGE:
            checkout.productFamily && checkout.grade
                ? {
                      id: checkout.productFamily.id,
                      gradeName: checkout.grade.name
                  }
                : null,
        PLAN_SELECT_PAGE: checkout.plan ? { id: checkout.plan.id } : null,
        ADDON_SELECT_PAGE: checkout.addon ? { id: checkout.addon.id } : null,
        GRADE_CURRICULUM_PAGE: checkout.grade
            ? {
                  id: checkout.grade.id,
                  gradeName: checkout.grade.name ?? null
              }
            : null,
        nextAttemptPage: checkout.nextAttemptPage
    };
    return atomData;
});

// Constructor function to create the mutation atom to avoid the atom state being shared across multiple containers
export const createUserCheckoutStateMutation = () =>
    atomsWithMutation(get => ({
        mutationKey: ["postUserCheckoutState"],
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/ban-types
        mutationFn: async (_data: {}) => {
            const userCheckoutState = get(userCheckoutStateAtom);
            const checkout = get(checkoutAtom) as CheckoutInterface;
            const userCheckoutStateToken = get(userCheckoutStateTokenAtom);

            const postData: UserCheckoutStateApiInterface = {
                username: checkout.user!.email,
                type: "checkout",
                token_info: userCheckoutState
            };
            return (
                await postCheckoutStateApi(
                    postData,
                    userCheckoutStateToken as string
                )
            ).data;
        }
    }));

export const loadableUserCheckoutStateApiAtom = loadable(
    userCheckoutApiStateAtom
);

interface ChargebeeTrackingData {
    event: string;
    [key: string]: string | null | undefined | { [key: string]: any };
}

export const chargebeeTrackingAtom = atom<ChargebeeTrackingData | null>(null);

export const chargebeeTrackingWithEffectAtom = atom(null, get => {
    const chargebeeTracking = get(chargebeeTrackingAtom);

    const onPushDataLayerItem = () => {
        if (chargebeeTracking) {
            window.dataLayer = window.dataLayer || [];

            const existingEventIndex = window.dataLayer.findIndex(
                (item: ChargebeeTrackingData) =>
                    item.event === chargebeeTracking.event
            );

            if (existingEventIndex !== -1) {
                // Replace the existing event object with the new one
                window.dataLayer[existingEventIndex] = chargebeeTracking;
            } else {
                // Add the new event object to the dataLayer
                window.dataLayer.push(chargebeeTracking);
            }
        }
    };

    onPushDataLayerItem();
});

export interface HeapTrackingData {
    eventName: string;
    data: any;
}

export const heapTrackingAtom = atom<HeapTrackingData | null>(null);

// Defining debug info for the atoms created to be consumed by the Jotai DevTools
checkoutAtom.debugLabel = "checkoutAtom";
checkoutTotalAmountAtom.debugLabel = "checkoutTotalAmountAtom";
couponAtom.debugLabel = "couponAtom";
canShowPaymentSuccessPageAtom.debugLabel = "canShowPaymentSuccessPageAtom";
canShowExistingUserPageAtom.debugLabel = "canShowExistingUserPageAtom";
userCheckoutStateTokenAtom.debugLabel = "userCheckoutStateTokenAtom";
userCheckoutApiStateAtom.debugLabel = "userCheckoutApiStateAtom";
createPaymentIntentAtom.debugLabel = "createPaymentIntentAtom";
createSubscriptionAtom.debugLabel = "createSubscriptionAtom";
validateCouponAtom.debugLabel = "validateCouponAtom";
userCheckoutStateAtom.debugLabel = "userCheckoutStateAtom";
isPaymentPendingAtom.debugLabel = "isPaymentPendingAtom";
canAddonSelectionSkipAtom.debugLabel = "canAddonSelectionSkipAtom";
isProvidedQueryParamsAtom.debugLabel = "isProvidedQueryParamsAtom";
isNewsletterSubEnabledAtom.debugLabel = "isNewsletterSubEnabledAtom";
pageQueryParamsAtom.debugLabel = "pageQueryParamsAtom";
loadableUserCheckoutStateApiAtom.debugLabel =
    "loadableUserCheckoutStateApiAtom";
loadableUserCheckoutStateApiAtom.debugLabel =
    "loadableUserCheckoutStateApiAtom";
chargebeeTrackingAtom.debugLabel = "chargebeeTrackingAtom";
chargebeeTrackingWithEffectAtom.debugLabel = "chargebeeTrackingWithEffectAtom";
heapTrackingAtom.debugLabel = "heapTrackingAtom";
parentIdAtom.debugLabel = "parentIdAtom";
